"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useCallback, useEffect, useState } from "react";

import {
  type IInternshipCategory,
  type IInternshipCourse,
} from "@/api/internshipData";
import {
  enrolInternshipUser,
  getInternshipTransaction,
  type IInternshipEnrolment,
  type IInternshipEnrolmentResponse,
  type IInternshipTransaction,
} from "@/api/internshipPayment";

import CourseProjectForm, {
  type CourseSelectionValues,
} from "./CourseProjectForm";
import FormProgressIndicator from "./FormProgressIndicator";
import PaymentInfoForm from "./PaymentInfoForm";
import StudentInfoForm, { type StudentInfoFormValues } from "./StudentInfoForm";

/**
 * The Apply Now modal — ported from the source's `Internship/ApplyForm/index`.
 *
 * Three steps: student details → course selection → payment. Radix Dialog is
 * used directly rather than this project's `atoms/Dialog`, because the source
 * styles its own header bar and suppresses the default close affordances; the
 * classes below are what the source's shadcn `DialogContent`/`Header`/`Title`
 * actually render after `twMerge`, with `sm:` (640px) translated to
 * `min-[640px]:` per `@/widgets/internship/container`.
 *
 * ── Returning from Atom ─────────────────────────────────────────────────────
 * Atom redirects back to /internship?transactionId=…, so the modal can also be
 * opened straight onto step 3 to show an outcome. The backend uses the sentinels
 * "failed" and "cancelled" in place of a real id when checkout did not complete.
 */

interface ApplyFormProps {
  open: boolean;
  onClose: () => void;
  /** The card the student clicked Apply on; preselects step 2. */
  preselectedCourse?: IInternshipCourse;
  categories: IInternshipCategory[];
  /** Present when Atom has redirected back. */
  transactionId?: string;
}

/** Sentinels the backend redirects with when checkout did not complete. */
const FAILED_SENTINELS = new Set(["failed", "cancelled"]);

/** The source's shadcn `DialogContent` base + its call-site overrides. */
const DIALOG_CONTENT_CLASSNAME = [
  "fixed left-[50%] top-[50%] z-50 w-full max-w-lg min-[640px]:max-w-[750px]",
  "translate-x-[-50%] translate-y-[-50%] gap-4",
  "border border-slate-200 bg-[#FFFFFF] shadow-lg min-[640px]:rounded-lg",
  "max-h-[90vh] overflow-hidden p-0 flex flex-col",
].join(" ");

/** Collected across steps 1 and 2 before enrolment. */
type ApplyFormData = Partial<StudentInfoFormValues> &
  Partial<Omit<CourseSelectionValues, "course">> & {
    course?: IInternshipCourse;
  };

export default function ApplyForm({
  open,
  onClose,
  preselectedCourse,
  categories,
  transactionId,
}: ApplyFormProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<ApplyFormData>({});
  const [transactionDetails, setTransactionDetails] = useState<
    IInternshipTransaction | undefined
  >(undefined);
  const [enrollmentError, setEnrollmentError] = useState("");
  const [isFetchingTransactionDetails, setIsFetchingTransactionDetails] =
    useState(Boolean(transactionId));
  const [isEnrollmentLoader, setIsEnrollmentLoader] = useState(false);
  const [enrollmentResponse, setEnrollmentResponse] =
    useState<IInternshipEnrolmentResponse | null>(null);

  // Coming back from Atom: jump to step 3 and resolve the outcome.
  useEffect(() => {
    if (!transactionId) {
      setIsFetchingTransactionDetails(false);
      return;
    }

    setStep(3);
    setIsFetchingTransactionDetails(true);

    if (FAILED_SENTINELS.has(transactionId)) {
      setTransactionDetails({ paymentStatus: "FAILED" });
      setIsFetchingTransactionDetails(false);
      return;
    }

    const controller = new AbortController();

    getInternshipTransaction(transactionId, controller.signal).then((result) => {
      // A failed lookup is shown as a failed payment rather than dropping the
      // student back into an empty payment form — as the source does.
      setTransactionDetails(result ?? { paymentStatus: "FAILED" });
      setIsFetchingTransactionDetails(false);
    });

    return () => controller.abort();
  }, [transactionId]);

  const handleStudentInfo = useCallback((data: StudentInfoFormValues) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setStep(2);
  }, []);

  const handleCourseSelection = useCallback((data: CourseSelectionValues) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setStep(3);
  }, []);

  const handlePaymentSubmit = useCallback(async () => {
    setIsEnrollmentLoader(true);
    setEnrollmentError("");

    // `course` and `courseCategory` are UI-only; the service takes courseId.
    // The source deletes them from the payload for the same reason.
    const {
      course: _course,
      courseCategory: _courseCategory,
      dob,
      ...studentAndCourse
    } = formData;

    const payload = {
      ...studentAndCourse,
      dob: dob ? new Date(dob).toISOString() : "",
    } as IInternshipEnrolment;

    const result = await enrolInternshipUser(payload);
    setIsEnrollmentLoader(false);

    if (result.ok) {
      // PaymentInfoForm watches this and opens Atom checkout.
      setEnrollmentResponse(result.data);
    } else {
      setEnrollmentError(result.message);
    }
  }, [formData]);

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80" />
        <DialogPrimitive.Content
          className={DIALOG_CONTENT_CLASSNAME}
          // The source pins the dialog open: no outside click, no Escape. The
          // only way out is the header's close button.
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="flex flex-col space-y-1.5 px-6 py-6 bg-[#9F1C33] text-white sticky top-0 z-10 flex-shrink-0">
            <DialogPrimitive.Title className="text-[22.559px] not-italic font-semibold leading-[normal] tracking-tight text-left">
              Apply Now Form
            </DialogPrimitive.Title>
            {/* The source comments its close button out, which leaves the modal
                with no exit. Kept here so the student is never trapped. */}
            <DialogPrimitive.Close
              className="absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white"
              aria-label="Close"
            >
              {/* lucide X, inlined */}
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </DialogPrimitive.Close>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <FormProgressIndicator
              currentStep={step}
              paymentStatus={transactionDetails?.paymentStatus}
            />

            {step === 1 ? (
              <StudentInfoForm
                onSubmit={handleStudentInfo}
                defaultValues={formData}
              />
            ) : null}

            {step === 2 ? (
              <CourseProjectForm
                onSubmit={handleCourseSelection}
                onBack={() => setStep(1)}
                defaultValues={formData}
                preselectedCourse={preselectedCourse}
                categories={categories}
              />
            ) : null}

            {step === 3 ? (
              <PaymentInfoForm
                onBack={() => {
                  setEnrollmentError("");
                  setStep(2);
                }}
                onPaymentSubmit={handlePaymentSubmit}
                course={formData.course}
                transactionDetails={transactionDetails}
                enrollmentError={enrollmentError}
                isFetchingTransactionDetails={isFetchingTransactionDetails}
                isEnrollmentLoader={isEnrollmentLoader}
                enrollmentResponse={enrollmentResponse}
              />
            ) : null}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
